import { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import { isArray } from 'lodash';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';

import Autocomplete from 'components/Autocomplete';
import ButtonMenu from 'components/ButtonMenu';
import CustomButton from 'components/CustomButtons';
import CenterFlexBox from './CenterFlexBox';
import { DebouncedTextField } from './CustomFields';

const useStyles = makeStyles(theme=>({
    filterTagsContainer: {
        display: 'flex',
        justifyContent: 'flex-start',
        flexWrap: 'wrap',
        listStyle: 'none',
        width: "fit-content",
        gap: theme.spacing(1.5),
        margin: theme.spacing(0,4),
    },
    tag: {
        height: "28px",
        borderRadius: "6px",
        fontFamily: "inter",
        fontWeight: "500",
    },
    filterRoot: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: theme.spacing(2,4),
      marginBottom: 0,
      backgroundColor: "#e8f0fe42",
      border: "1px solid lightgrey",
      borderRadius: theme.spacing(1),
    },
    filters: {
      padding: theme.spacing(2,0),
      display:'flex' , flexDirection:'column', 
      gap: theme.spacing(3)
    },
    clearFilters: {
      display:'flex' , justifyContent:'flex-end' , 
      padding:theme.spacing(0,2.5)
    },
    filterBox: {
      display: 'flex',
      alignItems: 'center',
      background: '#F1F2F6',
      padding: '5px 8px',
      borderRadius: '5px',
      color: '#464545',
    }
}));

const FilterTags = ({ tags = [], onRemove = () => { } }) => {
    const classes = useStyles();

    const handleDelete = (tag) => (e) => {
        tag.delete = true;
        onRemove(tag);
    };

    return (
        <Box className={classes.filterTagsContainer}>
            {tags.map((tag) => !tag.delete ? (
                <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    key={tag.id}
                    className={classes.tag}
                    label={tag.label}
                    onDelete={handleDelete(tag)}
                />
            ) : null)}
        </Box>)
}

export default function Filter({
    filters = [], onChange = () => { }, filterOptions = {}, appliedFilters = {}, personalizedList, quickSearchField, columns = [], visibleColumns = [], setVisibleColumns = () => {},
    children,
    showCategory = false
}) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [categoryAnchorEl, setCategoryAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const categoryOpen = Boolean(categoryAnchorEl);
    const [tags, setTags] = useState([]);

    // For category/column filter integration (now uses visibleColumns prop)
    const selectedColumns = columns.filter(col => visibleColumns.includes(col.field || col.id));

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Category button handlers
    const handleCategoryClick = (event) => {
        setCategoryAnchorEl(event.currentTarget);
    };
    const handleCategoryClose = () => {
        setCategoryAnchorEl(null);
    };
    const handleCategoryToggle = (field) => {
        if (visibleColumns.includes(field)) {
            setVisibleColumns(visibleColumns.filter(f => f !== field));
        } else {
            setVisibleColumns([...visibleColumns, field]);
        }
    };
    const handleCategoryChipDelete = (field) => {
        setVisibleColumns(visibleColumns.filter(f => f !== field));
    };

    const handleFilterChange = (filter) => (v, option) => {
        const isSelect = filter.type === 'select';
        if (isSelect && !option) return;
        const value = isSelect ? option.id : v;
        onChange({ ...filter, value });
    };

    const handleFilterRemove = (tag) => {
        if (tag.isCategory && tag.colField) {
            setVisibleColumns(visibleColumns.filter(f => f !== tag.colField));
        } else {
            onChange({ field: tag.id, value: undefined });
        }
    };

    const handleClearAllFilters = () => {
        filters.forEach(filter => {
            onChange({ field: filter.field, value: undefined });
        });
        if (quickSearchField && appliedFilters[quickSearchField]) {
            onChange({ field: quickSearchField, value: undefined });
        }
        handleClose();
    };

    useEffect(() => {
        const filterEntries = Object.entries(appliedFilters);
        let filterTags = [];
        if (filterEntries.length !== 0) {
            filterTags = filters
                .filter(f => (
                    appliedFilters[f.field] !== undefined && appliedFilters[f.field] !== null &&
                    !(isArray(appliedFilters[f.field]) && appliedFilters[f.field].length > 1)
                ))
                .map(({ field, label, type, ...f }) => {
                    let v = appliedFilters[field];
                    if (type === "select") {
                        v = filterOptions[field]?.find(fo => fo.id === v)?.label;
                    }
                    return { ...f, id: field, label: `${label}: ${v}` };
                });
            if (quickSearchField && appliedFilters[quickSearchField]) {
                const searchFieldInFilters = filters.find(f => f.field === quickSearchField);
                const searchLabel = searchFieldInFilters?.label || 'Search';
                const searchTag = {
                    id: quickSearchField,
                    label: `${searchLabel}: ${appliedFilters[quickSearchField]}`
                };
                if (!filterTags.some(tag => tag.id === quickSearchField)) {
                    filterTags = [...filterTags, searchTag];
                }
            }
        }
        // Add category/column tags only if showCategory is true
        if (showCategory && columns.length > 0 && visibleColumns.length > 0) {
            const categoryTags = columns
                .filter(col => visibleColumns.includes(col.field || col.id))
                .map(col => ({ id: `category_${col.field || col.id}`, label: col.label, isCategory: true, colField: col.field || col.id }));
            // Avoid duplicates if a filter and a column have the same field
            const allTags = [...filterTags, ...categoryTags.filter(catTag => !filterTags.some(tag => tag.label === catTag.label))];
            setTags(allTags);
        } else {
            setTags(filterTags);
        }
    }, [appliedFilters, filterOptions, filters, quickSearchField, columns, visibleColumns, showCategory]);

    // Category button label logic
    let categoryLabel = 'Category';
    if (selectedColumns.length > 1) {
      categoryLabel = 'Category: Multiple';
    } else if (selectedColumns.length === 1) {
      categoryLabel = `Category: ${selectedColumns[0].label}`;
    }
    return (
      <Box className={classes.filterRoot}>
        <Box display='flex' flexDirection='column' width='100%'>
          {/* Top row: search and buttons */}
          <Box display='flex' flexDirection='row' alignItems='flex-start' justifyContent='space-between' width='100%'>
            {/* Left: Search */}
            <Box display='flex' flexDirection='column' flex={1}>
              {children}
            </Box>
            {/* Right: Buttons */}
            <Box display='flex' flexDirection='row' alignItems='center' gap={2} ml={2}>
            {(filters.length > 0 || quickSearchField) && (
            <CenterFlexBox justifyContent="flex-end">
                <CustomButton
                    disableElevation
                    id="scheduled-button"
                    variant="outlined"
                    aria-controls={open ? "scheduled-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    startIcon={<AddRoundedIcon />}
                >
                    Add&nbsp;Filter
                </CustomButton>
                <ButtonMenu
                    id="scheduled-menu"
                    MenuListProps={{ "aria-labelledby": "scheduled-button" }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                >
                  <Box className={classes.filters}>
                    <Box className={classes.clearFilters}>
                        <Typography  
                            onClick={handleClearAllFilters}
                            sx={{fontSize:'16px' , fontWeight:700 , textDecoration:'underline',
                                cursor:'pointer'}} 
                            color='#1961BF'
                        >
                          Clear all filter
                        </Typography>
                    </Box>
                    {filters.map((filter, index) => (
                      <MenuItem disableRipple key={index} dense sx={{ px: 2, py: 0.5 }}>
                          <div style={{ minWidth: 100 }}>
                            {filter.label}
                          </div>&nbsp;&nbsp;
                          {
                            filter.type === 'select' ?
                              <Autocomplete
                                  name={filter.field}
                                  size={200}
                                  options={filterOptions?.[filter.field] || []}
                                  openOnFocus={true}
                                  onChange={handleFilterChange(filter)}
                                  value={appliedFilters[filter.field] || 'na'}
                              />
                              : 
                              <DebouncedTextField
                                  name={filter.field}
                                  type={filter.type}
                                  placeholder={filter.placeholder || 'Search...'}
                                  onChange={handleFilterChange(filter)}
                              />
                          }
                      </MenuItem>
                    ))}
                  </Box>
                </ButtonMenu>
            </CenterFlexBox>
          )}
              {/* Category Button and Chips UI */}
              {showCategory && columns.length > 0 && (
                <Box display='flex' alignItems='center'>
                  <Box position='relative' ml={2}>
                    <CustomButton
                      disableElevation
                      id="category-button"
                      variant="outlined"
                      aria-controls={categoryOpen ? 'category-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={categoryOpen ? 'true' : undefined}
                      onClick={handleCategoryClick}
                      startIcon={<AddRoundedIcon />}
                    >
                      {categoryLabel}
                      <ArrowDropDownIcon fontSize="14px"/>
                    </CustomButton>
                    <Menu
                      id="category-menu"
                      anchorEl={categoryAnchorEl}
                      open={categoryOpen}
                      onClose={handleCategoryClose}
                      MenuListProps={{
                        'aria-labelledby': 'category-button',
                      }}
                    >
                      <Box id='categories'>
                        <Box display='flex' flexDirection='column' gap='4px' px='6px' fontSize='12px'>
                          {columns.map((col, index) => (
                            <label key={index} htmlFor={`filter${col.field || col.id}`} style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 2}}>
                              <input
                                checked={visibleColumns.includes(col.field || col.id)}
                                onChange={() => handleCategoryToggle(col.field || col.id)}
                                type='checkbox'
                                id={`filter${col.field || col.id}`}
                                name={col.field || col.id}
                              />
                              <Typography variant='body02-semiBold'>
                                {col.label}
                              </Typography>
                            </label>
                          ))}
                        </Box>
                      </Box>
                    </Menu>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          {/* Second row: Applied filters label and chips */}
          {(tags.length > 0) && (
            <Box mt={2} width='100%'>
              <Typography variant="body01-bold" >Applied filters:</Typography>
              <Box display='flex' gap='12px' alignItems='center' flexWrap='wrap' style={{overflowX: 'auto', maxWidth: '100%' , marginTop: '10px' }}>
                <FilterTags tags={tags} onRemove={handleFilterRemove} />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
}